import { now as serverNow } from '~/models/clock'
import { formatDateValue, parseIsoDate } from '~/models/dateTime'

/**
 * 保存フィルタ (localStorage の JSON) の **直列化・復元・不正値の既定値フォールバック**
 * を持つ module (AC-SC-031)。
 *
 * 画面は「どの項目を保存するか」(`defineFilterCodec` の `fields`) と既定値だけを宣言し、
 * JSON.parse・オブジェクト検査・項目ごとの検証・欠落/破損時の既定値への落とし方はここが
 * 隠す。`usePersistedFilters` (読み書きのタイミング) とは直交する。
 *
 * ## 復元の契約 (全画面共通)
 *
 * - raw が `undefined` / JSON として壊れている / オブジェクトでない (配列を含む)
 *   → 全項目が既定値。
 * - 既定は **項目ごとに独立して** 不正値を既定値へ落とす (1 項目の破損で他を捨てない)。
 *   `onInvalid: 'all'` を宣言した画面だけ、 1 項目でも不正なら全項目を既定値へ戻す。
 * - 未知のキーは無視する。保存時は宣言した項目だけを宣言順に書く。
 * - キー欠落は既定値へ落とす。 例外は `optional()` (`wire: 'omit'`、既定) の項目で、
 *   キー欠落を「絞り込みなし (`undefined`)」と読む (保存側が undefined をキーごと落とすため)。
 *
 * ## 保存 key の version
 *
 * 保存形式を互換のない形に変える時は、 画面側の storage key の末尾を `-v2` のように
 * 上げる (前例: 取引契約管理)。 旧 key は読まず、消しもしない (= 保存済みフィルタを一度
 * だけ破棄する)。 互換のある形の変更 (項目の追加・enum の拡張) は key を上げず、
 * 本 module の「不正値は既定へ」で吸収する。 key は codec ではなく利用側 (hook の
 * `storageKey`) が持つ。
 */

/** `decode` が値を受理しなかったことを示す番兵 (null / undefined は正当な値になりうるため)。 */
export const INVALID = Symbol('filterCodec.invalid')
export type Invalid = typeof INVALID

export type DecodeContext = {
  /** 復元時刻 (時計依存の検証・補正用)。 */
  now: Date
}

/** 1 項目ぶんの codec。 `encode` は JSON に載せる値 (undefined ならキーごと落ちる) を返す。 */
export type FieldCodec<T> = {
  encode: (value: T) => unknown
  decode: (wire: unknown, ctx: DecodeContext) => T | Invalid
}

export type FilterCodec<T> = {
  serialize: (value: T) => string
  deserialize: (raw: string | undefined, now?: Date) => T
}

type Fields<T> = { [K in keyof T]-?: FieldCodec<T[K]> }

export function defineFilterCodec<T extends object>(options: {
  defaults: (now: Date) => T
  fields: Fields<T>
  /**
   * 不正な項目の扱い。 `'field'` (既定) は項目単位で既定値へ、 `'all'` は 1 項目でも
   * 不正なら全項目を既定値へ戻す。
   */
  onInvalid?: 'field' | 'all'
  /** 項目をまたぐ整合性 (false なら全項目を既定値へ戻す)。 */
  validate?: (value: T) => boolean
}): FilterCodec<T> {
  const { fields, onInvalid = 'field', validate } = options
  const keys = Object.keys(fields) as Array<keyof T & string>

  return {
    serialize: (value) => {
      const wire: Record<string, unknown> = {}
      for (const key of keys) wire[key] = fields[key].encode(value[key])
      return JSON.stringify(wire)
    },

    deserialize: (raw, now = serverNow()) => {
      // 共有定数を返す defaults でも新しい object にする (usePersistedFilters は「未操作」を
      // 初期 state の参照同一性で判定するため、 リセットで同じ定数を渡すと保存されない)。
      const defaults = { ...options.defaults(now) }
      if (raw === undefined) return defaults
      const record = parseJsonRecord(raw)
      if (record === undefined) return defaults

      const ctx: DecodeContext = { now }
      const restored = { ...defaults }
      for (const key of keys) {
        const decoded = fields[key].decode(record[key], ctx)
        if (decoded === INVALID) {
          if (onInvalid === 'all') return defaults
          continue
        }
        restored[key] = decoded
      }
      if (validate !== undefined && !validate(restored)) return defaults
      return restored
    },
  }
}

/**
 * JSON 文字列をオブジェクト (非配列) として読む。 項目ごとの宣言に収まらない形
 * (BalanceDemandDetail の token 列) の画面が `defineFilterCodec` を使わずに借りる。
 * 壊れている / オブジェクトでなければ undefined。
 */
export function parseJsonRecord(
  raw: string,
): Record<string, unknown> | undefined {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return undefined
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return undefined
  }
  return parsed as Record<string, unknown>
}

/** 任意の `decode` / `encode` から項目 codec を作る (標準の項目種別で足りない時だけ)。 */
export function field<T>(
  decode: (wire: unknown, ctx: DecodeContext) => T | Invalid,
  encode: (value: T) => unknown = (value) => value,
): FieldCodec<T> {
  return { encode, decode }
}

// ---------------------------------------------------------------------------
// 項目種別
// ---------------------------------------------------------------------------

/** 候補集合の 1 つ (enum・文字列 union・数値候補)。 候補外は不正。 */
export function oneOf<T extends string | number>(
  valid: Iterable<T>,
): FieldCodec<T> {
  const set = new Set<unknown>(valid)
  return field((wire) => (set.has(wire) ? (wire as T) : INVALID))
}

/**
 * proto enum の全メンバーを列挙する。UIが全メンバーを選択肢として表示する項目で使う。
 * UIが一部だけを表示する場合は、その選択肢の定義をUIと `oneOf` で共有する。
 * enum全値を復元してから隠れた値を補正する形にはしない（未選択表示の絞り込みを防ぐ）。
 * 全値を表示する項目では手で再列挙せず、enum追加時の拾い漏れを避ける。
 * 既定は `UNSPECIFIED` を除く（「すべて」は `undefined` 等で表すため）。除外は
 * キー名で行う（値と0の比較は `no-unsafe-enum-comparison` に当たる）。
 */
export function protoEnumValues<E extends Record<string, string | number>>(
  protoEnum: E,
  options: { includeUnspecified?: boolean } = {},
): Array<Extract<E[keyof E], number>> {
  return Object.entries(protoEnum)
    .filter(
      ([key, v]) =>
        typeof v === 'number' &&
        (options.includeUnspecified === true || key !== 'UNSPECIFIED'),
    )
    .map(([, v]) => v as Extract<E[keyof E], number>)
}

/** 整数 (小数・NaN・Infinity・非数値は不正)。 */
export function integer(): FieldCodec<number> {
  return field((wire) =>
    typeof wire === 'number' && Number.isInteger(wire) ? wire : INVALID,
  )
}

export function bool(): FieldCodec<boolean> {
  return field((wire) => (typeof wire === 'boolean' ? wire : INVALID))
}

export function text(): FieldCodec<string> {
  return field((wire) => (typeof wire === 'string' ? wire : INVALID))
}

/** 空文字を不正にする文字列 (空 = 「未選択」を別の表現で持つ項目用)。 */
export function nonEmptyText(): FieldCodec<string> {
  return field((wire) =>
    typeof wire === 'string' && wire !== '' ? wire : INVALID,
  )
}

/** 型ガードを通る値だけ受理する。 */
export function guarded<T>(guard: (v: unknown) => v is T): FieldCodec<T> {
  return field((wire) => (guard(wire) ? wire : INVALID))
}

/**
 * 配列。 要素のうち不正なものは **捨てる** (配列自体が配列でなければ不正)。
 * 保存側は各要素を `item.encode` で書く。
 */
export function listOf<T>(item: FieldCodec<T>): FieldCodec<T[]> {
  return {
    encode: (value) => value.map((v) => item.encode(v)),
    decode: (wire, ctx) => {
      if (!Array.isArray(wire)) return INVALID
      const kept: T[] = []
      for (const v of wire) {
        const decoded = item.decode(v, ctx)
        if (decoded !== INVALID) kept.push(decoded)
      }
      return kept
    },
  }
}

/** `listOf` の Set 版 (JSON 上は配列)。 */
export function setOf<T>(item: FieldCodec<T>): FieldCodec<Set<T>> {
  const list = listOf(item)
  return {
    encode: (value) => list.encode([...value]),
    decode: (wire, ctx) => {
      const decoded = list.decode(wire, ctx)
      return decoded === INVALID ? INVALID : new Set(decoded)
    },
  }
}

function maybe<T, E extends null | undefined>(
  inner: FieldCodec<T>,
  empty: E,
  emptyWire: null | undefined,
): FieldCodec<T | E> {
  return {
    encode: (value) => (value === empty ? emptyWire : inner.encode(value as T)),
    // 保存側が書く表現 (null、または omit 時のキー欠落) も empty として読む。
    decode: (wire, ctx) =>
      wire === null || (emptyWire === undefined && wire === undefined)
        ? empty
        : inner.decode(wire, ctx),
  }
}

/**
 * 「絞り込みなし = `undefined`」 の項目。
 * `wire: 'omit'` (既定) はキーごと落とす、 `'null'` は `null` で書く (既存の保存形式に合わせる)。
 *
 * **`wire` はキー欠落の解釈も決める。** `'omit'` はキー欠落を「絞り込みなし」と読み
 * (既定値が undefined でなくても既定値にならない)、 `'null'` はキー欠落を不正 (= 既定値、
 * `onInvalid: 'all'` なら全項目が既定値) と読む。 既定値が undefined でない項目を後から
 * 足す時、 または既存項目の `wire` を変える時は、 既存の保存値の復元結果が変わる。
 */
export function optional<T>(
  inner: FieldCodec<T>,
  options: { wire?: 'omit' | 'null' } = {},
): FieldCodec<T | undefined> {
  return maybe(inner, undefined, options.wire === 'null' ? null : undefined)
}

/** 「絞り込みなし = `null`」 の項目 (`null` で書く)。 */
export function nullable<T>(inner: FieldCodec<T>): FieldCodec<T | null> {
  return maybe(inner, null, null)
}

/**
 * 暦日文字列 `YYYY-MM-DD` ↔ 任意の型。 `parse` は形式のみ検証して存在しない暦日
 * ("2026-02-31" → 3/3) を繰り上げて受理することがあるので、 **往復して元の文字列に
 * 一致する**ことを確かめる。 実在しない暦日・形式不正は不正。
 */
export function calendarDate<T>(options: {
  parse: (ymd: string) => T | undefined | null
  format: (value: T) => string
}): FieldCodec<T> {
  return field<T>(
    (wire) => {
      if (typeof wire !== 'string') return INVALID
      const parsed = options.parse(wire)
      if (parsed === undefined || parsed === null) return INVALID
      return options.format(parsed) === wire ? parsed : INVALID
    },
    (value) => options.format(value),
  )
}

/** `calendarDate` の「in-memory 側も `YYYY-MM-DD` 文字列のまま持つ」版。 */
export function calendarDateString<T>(options: {
  parse: (ymd: string) => T | undefined | null
  format: (value: T) => string
}): FieldCodec<string> {
  const inner = calendarDate<T>(options)
  return field(
    (wire, ctx) => {
      const decoded = inner.decode(wire, ctx)
      return decoded === INVALID ? INVALID : (wire as string)
    },
    (value) => value,
  )
}

/** JS `Date` (JST 暦日) ↔ `YYYY-MM-DD`。 */
export function isoDate(): FieldCodec<Date> {
  return calendarDate({ parse: parseIsoDate, format: formatDateValue })
}
